import { ChevronDown, ChevronUp, Search, X, Video } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import frLocale from '@fullcalendar/core/locales/fr';
import AppointmentModal from '../components/modals/AppointmentModal';
import AppointmentDetailsModal from '../components/modals/AppointmentDetailsModal';
import { useDispatch, useSelector } from 'react-redux';
import { clearSelectedDoctor, getDoctorNameList, getDoctorById } from '../redux/doctorSlice';
import { getPatienttsNameList } from '../redux/patientSlice';
import { clearAllAppointments, clearError, getAllAppointments, getReschedulingSlotsByDoctor, rescheduleAppointment } from '../redux/appointmentSlice';
import { createRule, getSingleRule, deleteRule, clearRulesError } from '../redux/rulesSlice';
import ErrorPage from '../components/ErrorPage';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import CommonLoader from '../components/CommonLoader';
import errorimg from '../assets/img/colorkit (1).svg';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { format } from 'date-fns';
import MedicalHistory from '../components/patientTabs/MedicalHistory';
import { useTranslation } from 'react-i18next';
import { getDoctorMissingSetupItems } from '../utils/doctorSetupStatus';
import { getDoctorPastBoundary } from '../utils/pastBoundary';


//  Debounce Hook (exact copy from AppointmentList)
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

export default function Appointment() {
  const { t } = useTranslation('appointment');
  const lang = localStorage.getItem('i18nextLng');

  /* Redux state */
  const { doctorNameList, selectedDoctor, loading: loadingDoctor, error } = useSelector(s => s.doctor);
  const { selectedPatient, patientNameList, patientsLoading, error: patientsError } = useSelector(s => s.patient);
  const { appointments, allAppointmentsLoading, error: appointmenterror } = useSelector(s => s.appointment);
  const { rules, selectedRule, loading: rulesLoading, error: rulesError } = useSelector(s => s.rules);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* Refs & constants */
  const isInitialCall = useRef(true);
  const lastSelectedDoctor = useRef(null);
  const calendarRef = useRef(null);
  const currentDateRange = useRef({ start: null, end: null });
  const isApiCallInProgress = useRef(false);
  const lastFetchedRange = useRef({ doctorId: null, start: null, end: null });
  const datesSetTimeoutRef = useRef(null);
  const isInitialAppointmentLoad = useRef(true);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const isDraggingRef = useRef(false);

  const userRole = localStorage.getItem('Role');
  const currentDate = new Date();
  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  /* Local state */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [appoId, setAppoId] = useState('');
  const [isModalOpenAppointment, setisModalOpenAppointment] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDoctorState, setSelectedDoctorState] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');               // dropdown search
  const [searchError, setSearchError] = useState('');
  const [backgroundEvents, setBackgroundEvents] = useState([]);
  const [reschedulingSlots, setReschedulingSlots] = useState([]);
  const [reschedulePayload, setReschedulePayload] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1024);

  /* Calendar rule handling states */
  const [currentView, setCurrentView] = useState('timeGridWeek');
  const [weekToggleEnabled, setWeekToggleEnabled] = useState(false);
  const [currentRuleId, setCurrentRuleId] = useState(null);
  const [isPastWeek, setIsPastWeek] = useState(false);
  const isInitialRuleLoad = useRef(true);
  const lastFetchedWeekRule = useRef({ weekKey: null, enabled: false, ruleId: null });
  const ruleFetchInProgress = useRef(false);

  /* Maximum length for search input */
  const MAX_SEARCH_LENGTH = 50;

  /* Validate search input */
  const validateSearchInput = (value) => {
    // Remove any potentially dangerous characters
    const sanitized = value.replace(/[<>]/g, '');

    // Check length
    if (sanitized.length > MAX_SEARCH_LENGTH) {
      return t('appointment.search_exceed_limit', { limit: MAX_SEARCH_LENGTH });
    }

    // Check for only whitespace
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
      return t('appointment.search_only_spaces');
    }

    return '';
  };

  /* Handle search input change */
  const handleSearchChange = (e) => {
    const value = e.target.value;

    // Sanitize input - remove dangerous characters
    const sanitized = value.replace(/[<>]/g, '');
    const finalValue = sanitized.length > MAX_SEARCH_LENGTH
      ? sanitized.substring(0, MAX_SEARCH_LENGTH)
      : sanitized;

    // Always update searchQuery so the input stays in sync
    setSearchQuery(finalValue);

    // Validate input
    const error = validateSearchInput(finalValue);
    setSearchError(error);
  };

  /* Debounce for dropdown search (only if no error) */
  const debouncedSearch = useDebounce(searchQuery.trim(), 400);

  /* Resize handling */
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (datesSetTimeoutRef.current) clearTimeout(datesSetTimeoutRef.current);
    };
  }, []);

  /* Initial data load */
  useEffect(() => {
    if (isInitialCall.current) {
      if (userRole === 'admin') {
        dispatch(clearAllAppointments());
        dispatch(clearSelectedDoctor());
        dispatch(getDoctorNameList());               // initial list (no search)
      } else if (userRole === 'doctor') {
        const doctorId = localStorage.getItem('ID');
        dispatch(getDoctorById(doctorId));
        dispatch(getPatienttsNameList(doctorId));
      }
      isInitialCall.current = false;
    }
  }, [dispatch, userRole]);

  /* Search doctors when dropdown opens (only if no error) */
  useEffect(() => {
    if (userRole === 'admin' && isOpen && !searchError) {
      dispatch(getDoctorNameList(debouncedSearch));
    }
  }, [debouncedSearch, dispatch, userRole, isOpen, searchError]);

  /* Click-outside for dropdown */
  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setSearchError('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* Background events */
  useEffect(() => {
    if (selectedDoctor && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const view = calendarApi.view;
      setBackgroundEvents(generateBackgroundEvents(view.activeStart, view.activeEnd));
    } else {
      setBackgroundEvents([]);
    }
  }, [selectedDoctor, appointments, currentView, reschedulingSlots]);


  /* Helper: business hours */
  // Always returns the doctor's business hours array (used by FullCalendar to render the grey
  // non-business-hour shading). The booking restriction itself is gated separately via
  // `selectConstraint` and the `businessHour` checks in handleSelect/handleEventDrop —
  // so when the toggle is OFF, the grey stays visible but bookings are allowed.
  const getBusinessHours = () => {
    if (!selectedDoctor || !selectedDoctor.availability) return [];

    const businessHoursArray = [];

    selectedDoctor.availability.forEach(avail => {
      // Skip inactive availability
      if (!avail.isActive) return;

      // Get the doctor's day index (0 = Sunday, 1 = Monday, etc.)
      const doctorDayIndex = getDayIndex(avail.day);

      if (avail.startUTC && avail.endUTC) {
        // Use UTC times and compute the actual day in system timezone
        // startDayShift tells us if the UTC time is on a different day than the doctor's day
        // We need to figure out what day the start time falls on in the SYSTEM timezone
        const refDate = new Date();
        // Set refDate to the doctor's day of the current week
        const currentDay = refDate.getDay();
        const diff = doctorDayIndex - currentDay;
        refDate.setDate(refDate.getDate() + diff);
        const refDateStr = `${refDate.getFullYear()}-${String(refDate.getMonth() + 1).padStart(2, '0')}-${String(refDate.getDate()).padStart(2, '0')}`;

        // Build UTC moment for start and end
        let startUTCTime = avail.startUTC;
        if (startUTCTime && startUTCTime.includes('T')) {
          startUTCTime = startUTCTime.substring(11, 16);
        }
        let endUTCTime = avail.endUTC;
        if (endUTCTime && endUTCTime.includes('T')) {
          endUTCTime = endUTCTime.substring(11, 16);
        }

        const startUTCDate = new Date(`${refDateStr}T${startUTCTime}:00Z`);
        if (avail.startDayShift) startUTCDate.setDate(startUTCDate.getDate() + avail.startDayShift);
        const endUTCDate = new Date(`${refDateStr}T${endUTCTime}:00Z`);
        if (avail.endDayShift) endUTCDate.setDate(endUTCDate.getDate() + avail.endDayShift);

        // Get local day and time from the UTC dates (browser auto-converts)
        const localStartDay = startUTCDate.getDay();
        const localEndDay = endUTCDate.getDay();
        const startTime = `${String(startUTCDate.getHours()).padStart(2, '0')}:${String(startUTCDate.getMinutes()).padStart(2, '0')}`;
        const endTime = `${String(endUTCDate.getHours()).padStart(2, '0')}:${String(endUTCDate.getMinutes()).padStart(2, '0')}`;

        if (localStartDay === localEndDay) {
          // Same day in system timezone
          businessHoursArray.push({
            daysOfWeek: [localStartDay],
            startTime: startTime,
            endTime: endTime
          });
        } else {
          // Crosses midnight in system timezone - split into two entries
          businessHoursArray.push({
            daysOfWeek: [localStartDay],
            startTime: startTime,
            endTime: '23:59'
          });
          businessHoursArray.push({
            daysOfWeek: [localEndDay],
            startTime: '00:00',
            endTime: endTime
          });
        }
      } else {
        // Fallback: use local time strings directly
        const startTime = avail.start;
        const endTime = avail.end;
        businessHoursArray.push({
          daysOfWeek: [doctorDayIndex],
          startTime: startTime,
          endTime: endTime
        });
      }
    });

    return businessHoursArray;
  };

  const getDayIndex = day => [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ].indexOf(day);

  const formatToSystemTime = utcString => {
    // Convert UTC to system timezone using Luxon
    const dateTime = DateTime.fromISO(utcString, { zone: 'utc' }).setZone(systemTimeZone);
    return dateTime.toFormat('HH:mm');
  };

  /* generateBackgroundEvents */
  const generateBackgroundEvents = (startDate, endDate) => {
    if (!selectedDoctor || !selectedDoctor.availability) return [];

    const backgroundEvents = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const durationMinutes = reschedulePayload?.duration ? parseDuration(reschedulePayload.duration) : 120;

    // Background events are always generated so the grey shading remains visible. Each event
    // is tagged with `restrictionType` so booking-block paths can decide whether to enforce it
    // based on the corresponding doctorSettings toggle (see `isBgEventRestricted`).

    // MONTH VIEW: Use dayCellClassNames for styling, skip background events
    if (currentView === 'dayGridMonth') {
      return backgroundEvents; // Empty array, styling done via dayCellClassNames
    }

    // Extend range by 1 day before and after to handle timezone overflow
    // (e.g., doctor in France, system in India — Sunday inactive in France bleeds into Monday morning in India)
    const extendedStart = new Date(start);
    extendedStart.setDate(extendedStart.getDate() - 1);
    const extendedEnd = new Date(end);
    extendedEnd.setDate(extendedEnd.getDate() + 1);

    for (let date = new Date(extendedStart); date <= extendedEnd; date.setDate(date.getDate() + 1)) {
      const dayIndex = date.getDay();
      const dayName = [
        'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
      ][dayIndex];
      const avail = selectedDoctor.availability.find(a => a.day === dayName);

      if (avail) {
        if (avail.isActive) {
          avail.breaks.forEach((breakItem, index) => {
            const breakStartLocal = formatToSystemTime(breakItem?.breakStartUTC);
            const breakEndLocal = formatToSystemTime(breakItem?.breakEndUTC);
            const [startHour, startMinute] = breakStartLocal.split(/[.:]/).map(Number);
            const [endHour, endMinute] = breakEndLocal.split(/[.:]/).map(Number);

            const breakStart = new Date(date);
            breakStart.setHours(startHour, startMinute, 0, 0);
            const breakEnd = new Date(date);
            breakEnd.setHours(endHour, endMinute, 0, 0);

            backgroundEvents.push({
              id: `break-${avail.availabilityId}-${index}-${date.toISOString()}`,
              title: t('appointment.break_title'),
              start: breakStart.toISOString(),
              end: breakEnd.toISOString(),
              display: 'background',
              backgroundColor: '#d1d5db',
              classNames: ['disabled-slot'],
              editable: false,
              restrictionType: 'break'
            });
          });

          if (userRole !== 'patient' && isRescheduling && selectedAppointment) {
            let businessEnd;
            if (avail.endUTC) {
              let endUTCTime = avail.endUTC;
              if (endUTCTime.includes('T')) endUTCTime = endUTCTime.substring(11, 16);
              const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              businessEnd = new Date(`${dateStr}T${endUTCTime}:00Z`);
              if (avail.endDayShift) businessEnd.setDate(businessEnd.getDate() + avail.endDayShift);
            } else {
              const [endHour, endMinute] = avail.end.split(':').map(Number);
              businessEnd = new Date(date);
              businessEnd.setHours(endHour, endMinute, 0, 0);
            }
            const disableStart = new Date(businessEnd.getTime() - (durationMinutes - 15) * 60 * 1000);

            if (disableStart < businessEnd) {
              backgroundEvents.push({
                id: `disable-end-${avail.availabilityId}-${date.toISOString()}`,
                title: t('appointment.unavailable_title'),
                start: disableStart.toISOString(),
                end: businessEnd.toISOString(),
                display: 'background',
                backgroundColor: '#d1d5db',
                classNames: ['disabled-slot'],
                editable: false
              });
            }
          }
        } else if (userRole !== 'patient') {
          // Full day unavailable in doctor's timezone (00:00 to 24:00 doctor time)
          const docTz = selectedDoctor.timeZone || systemTimeZone;
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

          // Get the doctor's day start (00:00) and end (24:00) in doctor's timezone, then convert to UTC
          const doctorDayStart = DateTime.fromISO(`${dateStr}T00:00:00`, { zone: docTz });
          const doctorDayEnd = doctorDayStart.plus({ days: 1 });

          backgroundEvents.push({
            id: `inactive-day-${avail.availabilityId}-${date.toISOString()}`,
            title: t('appointment.unavailable_title'),
            start: doctorDayStart.toJSDate().toISOString(),
            end: doctorDayEnd.toJSDate().toISOString(),
            display: 'background',
            backgroundColor: '#d1d5db',
            classNames: ['disabled-slot'],
            editable: false,
            restrictionType: 'businessHour'
          });
        }
      }
    }

    if (selectedDoctor.leaveDates) {
      selectedDoctor.leaveDates.forEach((dateAvail, index) => {
        let leaveTitle = t('appointment.leave_full_day');
        let leaveStart, leaveEnd;

        // Use UTC times and convert to system timezone
        if (dateAvail.utcStart && dateAvail.utcEnd) {
          const utcStartDate = new Date(dateAvail.utcStart);
          const utcEndDate = new Date(dateAvail.utcEnd);
          leaveStart = utcStartDate;
          leaveEnd = utcEndDate;

          // Determine leave type for title
          if (dateAvail.startTime === '00:00' && dateAvail.endTime !== '23:59') {
            leaveTitle = t('appointment.leave_forenoon');
          } else if (dateAvail.startTime !== '00:00' && dateAvail.endTime === '23:59') {
            leaveTitle = t('appointment.leave_afternoon');
          }
        } else {
          // Fallback: use date string with local times
          const dateParts = dateAvail.date.substring(0, 10).split('-');
          const leaveDate = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));

          if (dateAvail.startTime === '00:00' && dateAvail.endTime !== '' && dateAvail.endTime !== '23:59') {
            const [endHour, endMinute] = dateAvail.endTime.split(':').map(Number);
            if (endHour <= 12) {
              leaveTitle = t('appointment.leave_forenoon');
              leaveStart = new Date(leaveDate);
              leaveStart.setHours(0, 0, 0, 0);
              leaveEnd = new Date(leaveDate);
              leaveEnd.setHours(endHour, endMinute, 0, 0);
            }
          } else if (dateAvail.startTime !== '' && dateAvail.startTime !== '00:00' && dateAvail.endTime === '23:59') {
            const [startHour, startMinute] = dateAvail.startTime.split(':').map(Number);
            if (startHour >= 12) {
              leaveTitle = t('appointment.leave_afternoon');
              leaveStart = new Date(leaveDate);
              leaveStart.setHours(startHour, startMinute, 0, 0);
              leaveEnd = new Date(leaveDate);
              leaveEnd.setHours(23, 59, 59, 999);
            }
          }

          if (!leaveStart || !leaveEnd) {
            leaveStart = new Date(leaveDate);
            leaveStart.setHours(0, 0, 0, 0);
            leaveEnd = new Date(leaveDate);
            leaveEnd.setHours(23, 59, 59, 999);
          }
        }

        // Check for any overlap with the visible range (not just leaveStart within range)
        // This handles timezone overflow where leave end bleeds into the next week
        if (leaveStart && leaveEnd && leaveStart < end && leaveEnd > start) {
          backgroundEvents.push({
            id: `inactive-${index}`,
            title: leaveTitle,
            start: leaveStart.toISOString(),
            end: leaveEnd.toISOString(),
            display: 'background',
            backgroundColor: '#d1d5db',
            classNames: ['disabled-slot'],
            editable: false,
            restrictionType: 'leave',
            // Public-holiday name (if the doctor marked this leave as one) — rendered
            // under the "Leave (…)" label in the calendar cell.
            holidayName: dateAvail.isPublicHoliday ? (dateAvail.holidayName || '') : ''
          });
        }
      });
    }

    // Rescheduling-recommendation reserved slots — show in gray with label
    if (Array.isArray(reschedulingSlots) && reschedulingSlots.length > 0) {
      reschedulingSlots.forEach((slot, index) => {
        if (!slot.utcStart || !slot.utcEnd) return;
        const slotStart = new Date(slot.utcStart);
        const slotEnd = new Date(slot.utcEnd);
        if (isNaN(slotStart.getTime()) || isNaN(slotEnd.getTime())) return;
        // Only include when the slot overlaps the visible range
        if (slotStart < end && slotEnd > start) {
          backgroundEvents.push({
            id: `reschedule-rec-${slot._id || `${slot.appointmentId}-${index}`}`,
            title: t('appointment.reschedule_recommended_title'),
            start: slotStart.toISOString(),
            end: slotEnd.toISOString(),
            display: 'background',
            backgroundColor: '#9ca3af',
            classNames: ['disabled-slot'],
            editable: false
          });
        }
      });
    }

    return backgroundEvents;

  };

  /* Doctor selection handlers */
  const handleReset = () => {
    setSelectedDoctorState(null);
    setIsOpen(false);
    setSearchQuery('');
    setSearchError('');
    setBackgroundEvents([]);
    setReschedulingSlots([]);
    setReschedulePayload(null);
    lastFetchedRange.current = { doctorId: null, start: null, end: null };
    dispatch(clearSelectedDoctor());
  };

  const handleSelectDoctor = doctor => {
    setSelectedDoctorState(doctor);
    setIsOpen(false);
    setSearchQuery('');
    setSearchError('');
    if (doctor && lastSelectedDoctor.current !== doctor.doctorId) {
      lastSelectedDoctor.current = doctor.doctorId;
      lastFetchedRange.current = { doctorId: null, start: null, end: null };
      isInitialAppointmentLoad.current = true;
      dispatch(getDoctorById(doctor.doctorId));
      dispatch(getPatienttsNameList(doctor.doctorId));
    }
  };

  /* Helper: Format date to YYYY-MM-DD */
  const formatDate = d => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /* Helper: Get week start and end dates */
  const getWeekDates = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const weekStart = new Date(d.setDate(diff));
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return {
      startdate: formatDate(weekStart),
      enddate: formatDate(weekEnd)
    };
  };

  /* Helper: Check if a week is in the past */
  const isWeekInPast = (weekEndDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekEndDate);
    weekEnd.setHours(23, 59, 59, 999);
    return weekEnd < today;
  };

  /* Fetch rule data for week view only (for enable/disable toggle) */
  const fetchRuleData = async (view, start, end) => {
    if (!selectedDoctor) return;

    const doctorId = selectedDoctor.doctorId;

    // Only fetch rule data for week view to control enable/disable toggle
    if (view === 'timeGridWeek') {
      const weekDates = getWeekDates(start);
      const weekKey = `${weekDates.startdate}_${weekDates.enddate}`;

      // Check if week is in the past
      const weekEndDate = new Date(weekDates.enddate);
      const pastWeek = isWeekInPast(weekEndDate);
      setIsPastWeek(pastWeek);

      // Check cache first - if we already have data for this week, use it immediately
      if (lastFetchedWeekRule.current.weekKey === weekKey &&
        lastFetchedWeekRule.current.doctorId === doctorId) {
        setWeekToggleEnabled(lastFetchedWeekRule.current.enabled);
        setCurrentRuleId(lastFetchedWeekRule.current.ruleId);
        return;
      }

      // Prevent multiple simultaneous fetches
      if (ruleFetchInProgress.current) return;
      ruleFetchInProgress.current = true;

      try {
        const result = await dispatch(getSingleRule({
          doctorId,
          startdate: weekDates.startdate,
          enddate: weekDates.enddate
        })).unwrap();

        const enabled = result?.data && result.data.status === true;
        const ruleId = enabled ? result.data.ruleId : null;

        // Update cache
        lastFetchedWeekRule.current = {
          weekKey,
          doctorId,
          enabled,
          ruleId
        };

        setWeekToggleEnabled(enabled);
        setCurrentRuleId(ruleId);
      } catch (error) {
        console.error('Error fetching single rule:', error);
        setWeekToggleEnabled(false);
        setCurrentRuleId(null);
        // Update cache with failed state
        lastFetchedWeekRule.current = {
          weekKey,
          doctorId,
          enabled: false,
          ruleId: null
        };
      } finally {
        ruleFetchInProgress.current = false;
      }
    } else {
      // For other views, reset toggle state
      setWeekToggleEnabled(false);
      setCurrentRuleId(null);
      setIsPastWeek(false);
    }
  };

  /* Calendar date range handling */
  const handleDatesSet = dateInfo => {
    const view = dateInfo.view.type;
    setCurrentView(view);

    if (selectedDoctor) {
      setBackgroundEvents(generateBackgroundEvents(dateInfo.start, dateInfo.end));
    } else {
      setBackgroundEvents([]);
      setReschedulingSlots([]);
      return;
    }

    if (datesSetTimeoutRef.current) clearTimeout(datesSetTimeoutRef.current);

    // For week view, fetch rule data immediately (non-blocking) for faster UI response
    if (view === 'timeGridWeek' && selectedDoctor) {
      fetchRuleData(view, dateInfo.start, dateInfo.end);
    }

    datesSetTimeoutRef.current = setTimeout(() => {
      if (!selectedDoctor) return;

      const start = formatDate(new Date(dateInfo.start));
      const end = formatDate(new Date(dateInfo.end));

      currentDateRange.current = { start, end };

      const isSameRange =
        lastFetchedRange.current.doctorId === selectedDoctor.doctorId &&
        lastFetchedRange.current.start === start &&
        lastFetchedRange.current.end === end;

      if (!isSameRange && !isApiCallInProgress.current) {
        isApiCallInProgress.current = true;
        lastFetchedRange.current = { doctorId: selectedDoctor.doctorId, start, end };

        // Fetch appointments
        dispatch(getAllAppointments({ doctorId: selectedDoctor.doctorId, start, end })).finally(() => {
          isApiCallInProgress.current = false;
          isInitialAppointmentLoad.current = false;
        });

        // Fetch rescheduling-recommendation reserved slots for this range
        dispatch(getReschedulingSlotsByDoctor({ doctorId: selectedDoctor.doctorId, start, end }))
          .unwrap()
          .then(slots => setReschedulingSlots(Array.isArray(slots) ? slots : []))
          .catch(() => setReschedulingSlots([]));

        // Only fetch rule data if not already fetched above (for week view)
        if (view !== 'timeGridWeek') {
          fetchRuleData(view, dateInfo.start, dateInfo.end);
        }
      } else {
        isInitialAppointmentLoad.current = false;
        // Only fetch rule data if not already fetched above (for week view)
        if (view !== 'timeGridWeek') {
          fetchRuleData(view, dateInfo.start, dateInfo.end);
        }
      }
    }, 50); // Reduced timeout for faster response
  };

  /* Initial rule load when doctor is selected */
  useEffect(() => {
    if (selectedDoctor && calendarRef.current && isInitialRuleLoad.current) {
      const calendarApi = calendarRef.current.getApi();
      const view = calendarApi.view;
      const start = view.activeStart;
      const end = view.activeEnd;

      fetchRuleData(view.type, start, end);
      isInitialRuleLoad.current = false;
    }
  }, [selectedDoctor]);

  /* Toggle handler for Week view */
  const handleWeekToggleConfirm = async () => {
    if (!selectedDoctor || currentView !== 'timeGridWeek' || isPastWeek) return;

    const calendarApi = calendarRef.current.getApi();
    const currentDate = calendarApi.getDate();
    const weekDates = getWeekDates(currentDate);

    // Format dates for display
    const startDateFormatted = format(new Date(weekDates.startdate), 'MMM d, yyyy');
    const endDateFormatted = format(new Date(weekDates.enddate), 'MMM d, yyyy');

    // Show confirmation dialog
    const confirmationResult = await Swal.fire({
      title: weekToggleEnabled
        ? t('appointment.confirm_disable_week_title')
        : t('appointment.confirm_enable_week_title'),
      html: weekToggleEnabled
        ? t('appointment.confirm_disable_week_text', {
          startDate: startDateFormatted,
          endDate: endDateFormatted
        })
        : t('appointment.confirm_enable_week_text', {
          startDate: startDateFormatted,
          endDate: endDateFormatted
        }),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: weekToggleEnabled ? '#d33' : '#4ABDBD',
      cancelButtonColor: '#e5e7eb',
      confirmButtonText: weekToggleEnabled
        ? t('appointment.confirm_disable_button')
        : t('appointment.confirm_enable_button'),
      cancelButtonText: t('appointment.cancel_button'),
    });

    if (!confirmationResult.isConfirmed) {
      return;
    }

    // Proceed with the toggle
    await handleWeekToggle();
  };

  const handleWeekToggle = async () => {
    if (!selectedDoctor || currentView !== 'timeGridWeek' || isPastWeek) return;

    const calendarApi = calendarRef.current.getApi();
    const currentDate = calendarApi.getDate();
    const weekDates = getWeekDates(currentDate);
    const weekKey = `${weekDates.startdate}_${weekDates.enddate}`;

    // Double check if week is past (safety check)
    const weekEndDate = new Date(weekDates.enddate);
    if (isWeekInPast(weekEndDate)) {
      toast.warn(t('appointment.past_week_error') || 'Cannot modify settings for past weeks');
      return;
    }

    if (weekToggleEnabled) {
      // Toggle OFF - Delete Rule
      if (!currentRuleId) {
        console.error('No ruleId to delete');
        return;
      }

      try {
        await dispatch(deleteRule(currentRuleId)).unwrap();
        setWeekToggleEnabled(false);
        setCurrentRuleId(null);

        // Update cache
        lastFetchedWeekRule.current = {
          weekKey,
          doctorId: selectedDoctor.doctorId,
          enabled: false,
          ruleId: null
        };

        toast.success(t('appointment.rule_deleted_message') || 'Week rule deleted successfully');
      } catch (error) {
        console.error('Error deleting rule:', error);
        toast.warn(error?.message?.en || t('appointment.error_message') || 'Failed to delete rule');
      }
    } else {
      // Toggle ON - Create Rule
      try {
        const payload = {
          doctorId: selectedDoctor.doctorId,
          ruleType: 'week',
          startdate: weekDates.startdate,
          enddate: weekDates.enddate,
          status: true
        };

        const result = await dispatch(createRule(payload)).unwrap();

        if (result?.data?.ruleId) {
          setWeekToggleEnabled(true);
          setCurrentRuleId(result.data.ruleId);

          // Update cache
          lastFetchedWeekRule.current = {
            weekKey,
            doctorId: selectedDoctor.doctorId,
            enabled: true,
            ruleId: result.data.ruleId
          };

          toast.success(t('appointment.rule_created_message') || 'Week rule created successfully');
        }
      } catch (error) {
        console.error('Error creating rule:', error);
        toast.warn(error?.message?.en || t('appointment.error_message') || 'Failed to create rule');
      }
    }
  };

  /* Reset rule states when doctor changes */
  useEffect(() => {
    if (!selectedDoctor) {
      setWeekToggleEnabled(false);
      setCurrentRuleId(null);
      setIsPastWeek(false);
      isInitialRuleLoad.current = true;
      lastFetchedWeekRule.current = { weekKey: null, enabled: false, ruleId: null, doctorId: null };
    } else {
      // Clear cache when doctor changes
      lastFetchedWeekRule.current = { weekKey: null, enabled: false, ruleId: null, doctorId: null };
    }
  }, [selectedDoctor]);

  /* Store latest appointments in a ref for tooltip access */
  const appointmentsRef = useRef(appointments);
  useEffect(() => {
    appointmentsRef.current = appointments;
  }, [appointments]);

  /* Event creation */
  const parseDuration = duration => {
    const [hours, minutes] = duration.split(':').map(Number);
    return hours * 60 + (minutes || 0);
  };

  const allEvents = [
    ...(appointments?.map(appt => {
      // Use start & end UTC keys from API, convert to system timezone
      const startDateTime = DateTime.fromISO(appt.start, { zone: 'utc' }).setZone(systemTimeZone);
      const endDateTime = DateTime.fromISO(appt.end, { zone: 'utc' }).setZone(systemTimeZone);

      const isCompleted = appt.status === 'Completed';
      const isReschedulingStatus = appt.status === 'Rescheduling';
      const eventColor = isReschedulingStatus ? '#808080' : (isCompleted ? '#6B7280' : (appt.color || '#4ABDBD'));

      return {
        ...appt,
        id: appt.appointmentId,
        start: startDateTime.toISO(),
        end: endDateTime.toISO(),
        title: appt.appointTypeName || t('appointment.appointment_title'),
        backgroundColor: eventColor,
        borderColor: eventColor,
        editable: !isRescheduling && !!selectedDoctor && !isCompleted && !isReschedulingStatus,
      };
    }) ?? []),
    ...backgroundEvents,
  ];

  /* Drag-and-drop reschedule */
  const handleEventDrop = async info => {
    // Remove any tooltip that might be stuck after drag
    const existingTooltip = document.querySelector('.fc-event-tooltip');
    if (existingTooltip) existingTooltip.remove();

    if (!selectedDoctor) { info.revert(); return; }

    const appointment = info.event._def.extendedProps;
    // Use systemTimeZone (browser) for all comparisons since getBusinessHours() returns times in systemTimeZone
    const selectStart = DateTime.fromISO(info.event.startStr, { zone: systemTimeZone });
    const selectEnd = DateTime.fromISO(info.event.endStr, { zone: systemTimeZone });

    if (selectStart.toJSDate() < getDoctorPastBoundary(selectedDoctor?.doctorSettings, currentDate)) {
      info.revert();
      if (userRole !== 'patient') {
        toast.warn(t('appointment.invalid_past_date_message', { action: 'reschedule' }));
      }
      return;
    }

    const selectDate = selectStart.toJSDate().getDay();
    const selectTime = selectStart.hour * 60 + selectStart.minute;
    const selectEndTime = selectEnd.hour * 60 + selectEnd.minute;

    // Get doctorSettings to check businessHour restriction
    const doctorSettings = selectedDoctor?.doctorSettings;
    const businessHourRestriction = doctorSettings?.businessHour !== false; // Default to true if not set

    const businessHoursForDay = getBusinessHours().find(bh => bh.daysOfWeek.includes(selectDate));

    // Only validate business hours if businessHour restriction is enabled
    if (businessHourRestriction) {
      if (!businessHoursForDay) {
        info.revert();
        if (userRole !== 'patient') {
          toast.warn(t('appointment.unavailable_day_message'));
        }
        return;
      }

      const [startHour, startMinute] = businessHoursForDay.startTime.split(':').map(Number);
      const [endHour, endMinute] = businessHoursForDay.endTime.split(':').map(Number);
      const businessStartMinutes = startHour * 60 + startMinute;
      const businessEndMinutes = endHour * 60 + endMinute;

      const durationMinutes = selectEndTime - selectTime;

      if (selectTime < businessStartMinutes || selectEndTime > businessEndMinutes) {
        info.revert();
        return;
      }
    }

    for (const bgEvent of backgroundEvents) {
      if (!isBgEventRestricted(bgEvent)) continue;
      const bgStart = new Date(bgEvent.start);
      const bgEnd = new Date(bgEvent.end);
      if (isOverlapping(selectStart.toJSDate(), selectEnd.toJSDate(), bgStart, bgEnd)) {
        info.revert();
        if (userRole !== 'patient') {
          if (bgEvent.title === t('appointment.break_title')) {
            toast.warn(t('appointment.break_time_message', { action: 'reschedule' }));
          } else {
            toast.warn(t('appointment.unavailable_slot_message', { action: 'reschedule' }));
          }
        }
        return;
      }
    }

    for (const appt of appointments) {
      if (appt.id === appointment.id) continue;
      const apptStart = DateTime.fromISO(appt.start, { zone: 'utc' }).setZone(appt.timeZone || systemTimeZone);
      const apptEnd = DateTime.fromISO(appt.end, { zone: 'utc' }).setZone(appt.timeZone || systemTimeZone);
      if (isOverlapping(selectStart.toJSDate(), selectEnd.toJSDate(), apptStart.toJSDate(), apptEnd.toJSDate())) {
        info.revert();
        if (userRole !== 'patient') {
          toast.warn(t('appointment.conflict_message'));
        }
        return;
      }
    }

    const originalAddressType = appointment.addressType;
    const docTz = selectedDoctor?.timeZone || systemTimeZone;
    const newDay = selectStart.setZone(docTz).setLocale('en').toFormat('cccc');
    const newDayAvailability = selectedDoctor?.availability?.find(avail => avail.day === newDay);
    const newAddressType = newDayAvailability?.addressType;
    let isAddressMismatch = false;

    if (originalAddressType !== newAddressType) {
      if (userRole !== 'patient') {
        isAddressMismatch = true;
      } else {
        info.revert();
        return;
      }
    }

    const newDate = selectStart.toFormat('yyyy-MM-dd');
    const newTime = selectStart.toFormat('HH:mm');
    const newSlotEndTime = selectEnd.toFormat('HH:mm');

    const durationInMs = selectEnd.toJSDate() - selectStart.toJSDate();
    const durationInMinutes = Math.floor(durationInMs / 1000 / 60);
    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;
    const duration = `${hours}:${minutes.toString().padStart(2, '0')}`;

    const payload = {
      appointmentId: appointment.appointmentId,
      newDate,
      newTime,
      newSlotEndTime,
      duration: duration,
      timeZone: systemTimeZone,
      SpecialtyId: appointment.SpecialtyId,
      addressType: newAddressType || appointment.addressType,
      appointmentMode: appointment.appointmentMode,
      appointmentTypeId: appointment.appointmentType,
      description: appointment.description,
      doctorDescription: appointment.doctorDescription,
      doctorId: appointment.doctorId,
      patientId: appointment.patientId,
      price: appointment.price || 0,
      start: currentDateRange.current.start,
      end: currentDateRange.current.end,
    };

    if (userRole !== 'patient') {
      let title = t('appointment.confirm_reschedule_title');
      let text = t('appointment.confirm_reschedule_message', { date: newDate, time: newTime });

      if (isAddressMismatch) {
        title = t('appointment.address_mismatch_title') || 'Address Mismatch';
        text = `${t('appointment.address_mismatch_drag_text')}\n\n${text}`;
      }

      const result = await Swal.fire({
        title: title,
        text: text,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: t('appointment.yes_reschedule_button'),
        cancelButtonText: t('appointment.cancel_button'),
        confirmButtonColor: '#4ABDBD',
        cancelButtonColor: '#d33',
      });

      if (!result.isConfirmed) {
        info.revert();
        return;
      }
    }

    try {
      await dispatch(rescheduleAppointment(payload)).unwrap();
      toast.success(t('appointment.reschedule_success_message'));
      lastFetchedRange.current = { doctorId: null, start: null, end: null };
      dispatch(getAllAppointments({
        doctorId: selectedDoctor.doctorId,
        start: currentDateRange.current.start,
        end: currentDateRange.current.end
      }));
      dispatch(getReschedulingSlotsByDoctor({
        doctorId: selectedDoctor.doctorId,
        start: currentDateRange.current.start,
        end: currentDateRange.current.end
      }))
        .unwrap()
        .then(slots => setReschedulingSlots(Array.isArray(slots) ? slots : []))
        .catch(() => { });
    } catch (err) {
      console.error('Reschedule error:', err);
    }
  };

  const isOverlapping = (start1, end1, start2, end2) => {
    return start1 < end2 && start2 < end1;
  };

  // Decouples the visual grey shading from the booking restriction. Background events are
  // always rendered (grey stays visible), but bookings are only blocked when the matching
  // doctorSettings toggle is enabled. Untagged events (rescheduling helpers) always restrict.
  const isBgEventRestricted = (bgEvent) => {
    const settings = selectedDoctor?.doctorSettings;
    if (bgEvent.restrictionType === 'break') return settings?.breakRestriction !== false;
    if (bgEvent.restrictionType === 'leave') return settings?.leaveRestriction !== false;
    if (bgEvent.restrictionType === 'businessHour') return settings?.businessHour !== false;
    return true;
  };

  /* Refetch rescheduling-recommendation slots for the currently visible range */
  const refetchReschedulingSlots = () => {
    if (!selectedDoctor?.doctorId) return;
    const { start, end } = currentDateRange.current || {};
    if (!start || !end) return;
    dispatch(getReschedulingSlotsByDoctor({ doctorId: selectedDoctor.doctorId, start, end }))
      .unwrap()
      .then(slots => setReschedulingSlots(Array.isArray(slots) ? slots : []))
      .catch(() => { });
  };

  /* Slot selection */
  const handleSelect = async info => {
    if (!selectedDoctor) return;

    // Check if selection is allowed based on current view and rule status
    if (currentView === 'dayGridMonth') {
      // Month view - always disabled
      toast.warn(t('appointment.month_view_disabled_message') || 'Slot selection is disabled in month view');
      return;
    }

    if (currentView === 'timeGridDay') {
      // Day view - disabled
      toast.warn(t('appointment.calendar_disabled_message') || 'Slot selection is disabled in day view');
      return;
    }

    // Use systemTimeZone (browser) for all comparisons since getBusinessHours() returns times in systemTimeZone
    const selectStart = DateTime.fromISO(info.startStr, { zone: systemTimeZone });
    const selectEndForBg = DateTime.fromISO(info.endStr, { zone: systemTimeZone });

    // Check leave/break overlap BEFORE past date check
    for (const bgEvent of backgroundEvents) {
      if (!isBgEventRestricted(bgEvent)) continue;
      const bgStart = new Date(bgEvent.start);
      const bgEnd = new Date(bgEvent.end);
      if (isOverlapping(selectStart.toJSDate(), selectEndForBg.toJSDate(), bgStart, bgEnd)) {
        if (userRole !== 'patient') {
          if (bgEvent.title === t('appointment.break_title')) {
            toast.warn(t('appointment.break_time_message', { action: isRescheduling ? 'reschedule' : 'book' }));
          } else {
            toast.warn(t('appointment.unavailable_slot_message', { action: isRescheduling ? 'reschedule' : 'book' }));
          }
        }
        return;
      }
    }

    if (selectStart.toJSDate() < getDoctorPastBoundary(selectedDoctor?.doctorSettings, currentDate)) {
      if (userRole !== 'patient') {
        toast.warn(t('appointment.invalid_past_date_message', { action: isRescheduling ? 'reschedule' : 'book' }));
      }
      return;
    }

    const selectDate = selectStart.toJSDate().getDay();
    const selectTime = selectStart.hour * 60 + selectStart.minute;

    // Get doctorSettings to check businessHour restriction
    const doctorSettings = selectedDoctor?.doctorSettings;
    const businessHourRestriction = doctorSettings?.businessHour !== false; // Default to true if not set

    const businessHoursForDay = getBusinessHours().find(bh => bh.daysOfWeek.includes(selectDate));

    // Only validate business hours if businessHour restriction is enabled
    if (businessHourRestriction) {
      if (!businessHoursForDay) {
        if (userRole !== 'patient' && isRescheduling) {
          toast.warn(t('appointment.unavailable_day_message'));
        }
        return;
      }

      const [startHour, startMinute] = businessHoursForDay.startTime.split(':').map(Number);
      const [endHour, endMinute] = businessHoursForDay.endTime.split(':').map(Number);
      const businessStartMinutes = startHour * 60 + startMinute;
      const businessEndMinutes = endHour * 60 + endMinute;

      let adjustedEnd;
      let durationMinutes;

      if (isRescheduling && selectedAppointment && reschedulePayload) {
        durationMinutes = parseDuration(reschedulePayload.duration);
        const durationMs = durationMinutes * 60 * 1000;
        adjustedEnd = selectStart.plus({ milliseconds: durationMs });
      } else {
        const selectedApptType = selectedDoctor?.appointmentSpeciality?.[0]?.appointmentTypes?.[0] || {
          type: 'consultation',
          duration: '0:15',
          price: '300'
        };
        durationMinutes = parseDuration('0:15');
        const durationMs = durationMinutes * 60 * 1000;
        adjustedEnd = selectStart.plus({ milliseconds: durationMs });
      }

      const adjustedEndTime = adjustedEnd.hour * 60 + adjustedEnd.minute;

      if ((selectTime < businessStartMinutes || adjustedEndTime > businessEndMinutes) && isRescheduling) {
        if (userRole !== 'patient') {
          const endsPastBusinessHours = selectTime < businessEndMinutes && adjustedEndTime > businessEndMinutes;
          toast.warn(
            endsPastBusinessHours
              ? t('appointment.unavailable_time_message', { minutes: durationMinutes })
              : t('appointment.outside_business_hours_message')
          );
        }
        return;
      }
    }

    // Calculate adjustedEnd for break/leave validation even if business hours are not restricted
    let adjustedEnd;
    let durationMinutes;
    if (isRescheduling && selectedAppointment && reschedulePayload) {
      durationMinutes = parseDuration(reschedulePayload.duration);
      const durationMs = durationMinutes * 60 * 1000;
      adjustedEnd = selectStart.plus({ milliseconds: durationMs });
    } else {
      const selectedApptType = selectedDoctor?.appointmentSpeciality?.[0]?.appointmentTypes?.[0] || {
        type: 'consultation',
        duration: '0:15',
        price: '300'
      };
      durationMinutes = parseDuration('0:15');
      const durationMs = durationMinutes * 60 * 1000;
      adjustedEnd = selectStart.plus({ milliseconds: durationMs });
    }

    for (const bgEvent of backgroundEvents) {
      if (!isBgEventRestricted(bgEvent)) continue;
      const bgStart = new Date(bgEvent.start);
      const bgEnd = new Date(bgEvent.end);
      if (isOverlapping(selectStart.toJSDate(), adjustedEnd.toJSDate(), bgStart, bgEnd)) {
        if (userRole !== 'patient') {
          if (bgEvent.title === t('appointment.break_title')) {
            toast.warn(t('appointment.break_time_message', { action: isRescheduling ? 'reschedule' : 'book' }));
          } else {
            toast.warn(t('appointment.unavailable_slot_message', { action: isRescheduling ? 'reschedule' : 'book' }));
          }
        }
        return;
      }
    }

    for (const appt of appointments) {
      if (isRescheduling && appt.id === selectedAppointment?.id) continue;
      const apptStart = DateTime.fromISO(appt.start, { zone: 'utc' }).setZone(appt.timeZone || systemTimeZone);
      const apptEnd = DateTime.fromISO(appt.end, { zone: 'utc' }).setZone(appt.timeZone || systemTimeZone);
      if (isOverlapping(selectStart.toJSDate(), adjustedEnd.toJSDate(), apptStart.toJSDate(), apptEnd.toJSDate())) {
        if (userRole !== 'patient' && isRescheduling) {
          toast.warn(t('appointment.conflict_message'));
        }
        return;
      }
    }

    if (isRescheduling && reschedulePayload) {
      const originalAddressType = reschedulePayload.addressType;
      const newDay = selectStart.setLocale('en').toFormat('cccc');
      const newDayAvailability = selectedDoctor?.availability?.find(avail => avail.day === newDay);
      const newAddressType = newDayAvailability?.addressType;
      let isAddressMismatch = false;

      if (originalAddressType !== newAddressType) {
        if (userRole !== 'patient') {
          isAddressMismatch = true;
        } else {
          return;
        }
      }

      const newDate = selectStart.toFormat('yyyy-MM-dd');
      const newTime = selectStart.toFormat('HH:mm');
      const newSlotEndTime = adjustedEnd.toFormat('HH:mm');

      const updatedPayload = {
        ...reschedulePayload,
        newDate,
        newTime,
        newSlotEndTime,
        addressType: newAddressType || reschedulePayload.addressType,
        // Only set price and pricetype if they don't already exist in reschedulePayload
        // This preserves values that were already set in AppointmentModal
        price: reschedulePayload?.price ?? selectedAppointment?.price ?? 0,
        pricetype: reschedulePayload?.pricetype ?? 'Type 1',
        start: currentDateRange.current.start,
        end: currentDateRange.current.end,
      };

      if (userRole !== 'patient') {
        let title = t('appointment.confirm_reschedule_title');
        let text = t('appointment.confirm_reschedule_message', { date: newDate, time: newTime });

        if (isAddressMismatch) {
          title = t('appointment.address_mismatch_title') || 'Address Mismatch';
          text = `${t('appointment.address_mismatch_select_text')}\n\n${text}`;
        }

        const result = await Swal.fire({
          title: title,
          text: text,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: t('appointment.yes_reschedule_button'),
          cancelButtonText: t('appointment.cancel_button'),
          confirmButtonColor: '#4ABDBD',
          cancelButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;
      }

      try {
        await dispatch(rescheduleAppointment(updatedPayload)).unwrap();
        toast.success(t('appointment.reschedule_success_message'));
        setIsRescheduling(false);
        setReschedulePayload(null);
        setSelectedAppointment(null);
        setSelectedSlot(null);
        lastFetchedRange.current = { doctorId: null, start: null, end: null };
        dispatch(getAllAppointments({
          doctorId: selectedDoctor.doctorId,
          start: currentDateRange.current.start,
          end: currentDateRange.current.end
        }));
        dispatch(getReschedulingSlotsByDoctor({
          doctorId: selectedDoctor.doctorId,
          start: currentDateRange.current.start,
          end: currentDateRange.current.end
        }))
          .unwrap()
          .then(slots => setReschedulingSlots(Array.isArray(slots) ? slots : []))
          .catch(() => { });
      } catch (err) {
        console.error('Reschedule error:', err);
      }
    } else {
      // Validate address specialties and appointment types before opening modal
      const clickedDay = selectStart.setLocale('en').toFormat('cccc');
      const clickedDayAvailability = selectedDoctor?.availability?.find(avail => avail.day === clickedDay);
      const clickedAddressId = clickedDayAvailability?.addressType;
      const clickedAddress = selectedDoctor?.address?.find(addr => addr.addressId === clickedAddressId);

      // Check if address has no specialties
      if (!clickedAddress?.specialty || clickedAddress.specialty.length === 0) {
        const result = await Swal.fire({
          title: t('appointment.warning_title') || 'Warning',
          text: t('appointment.no_specialty_on_address') || `Unable to book an appointment on the selected slot. The address assigned to ${clickedDay} does not have any specialties configured.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: t('appointment.go_to_profile') || 'Go to Profile',
          cancelButtonText: t('appointment.cancel_button'),
          confirmButtonColor: '#4ABDBD',
          cancelButtonColor: '#d33',
        });
        if (result.isConfirmed) {
          navigate('/doctorProfile', { state: { tab: 'address' } });
        }
        return;
      }

      // Check if each specialty on this address has appointment types
      const specialtiesWithoutTypes = [];
      for (const spec of clickedAddress.specialty) {
        const matchingSpeciality = selectedDoctor?.appointmentSpeciality?.find(s => s.name === spec.name);
        if (!matchingSpeciality || !matchingSpeciality.appointmentTypes || matchingSpeciality.appointmentTypes.length === 0) {
          specialtiesWithoutTypes.push(spec.name);
        }
      }

      if (specialtiesWithoutTypes.length > 0) {
        const result = await Swal.fire({
          title: t('appointment.warning_title') || 'Warning',
          text: (t('appointment.no_appointment_type_on_specialty') || `Unable to book an appointment on the selected slot. The specialties assigned to this address on ${clickedDay} do not have any appointment types configured: `) + specialtiesWithoutTypes.join(', ') + '.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: t('appointment.go_to_profile') || 'Go to Profile',
          cancelButtonText: t('appointment.cancel_button'),
          confirmButtonColor: '#4ABDBD',
          cancelButtonColor: '#d33',
        });
        if (result.isConfirmed) {
          navigate('/doctorProfile', { state: { tab: 'specialities' } });
        }
        return;
      }

      setSelectedSlot({ start: selectStart.toISO(), end: adjustedEnd.toISO() });
      if (isRescheduling) setSelectedAppointment(selectedAppointment);
      setIsModalOpen(true);
    }
  };

  /* Event click */
  const handleEventClick = async info => {
    if (isRescheduling || !selectedDoctor || info.event.display === 'background') return;
    const appointment = info.event._def.extendedProps;
    setSelectedAppointment(appointment);

    if (appointment.status === 'Completed') {
      setIsDetailsModalOpen(true);
    } else {
      setSelectedSlot({ start: appointment.start, end: appointment.end });
      setIsModalOpen(true);
      setIsRescheduling(false);
    }
  };

  const handleReschedulePayload = payload => {
    setReschedulePayload(payload);
  };

  /* Render */
  if ((error?.status >= 500 && error?.status <= 599) || error?.message === 'Network Error') {
    return <ErrorPage error={error} pageName="Appointment Page" id="Appointment-error-page" />;
  }

  const handleCloseOpenAppointment = () => setisModalOpenAppointment(false);

  /* Handle appointment errors with useEffect to prevent infinite loops */
  const hasShownError = useRef(false);
  useEffect(() => {
    if (appointmenterror && !hasShownError.current) {
      hasShownError.current = true;

      // Extract error message handling nested message structure
      let errorMessage = t('appointment.error_message'); // Default fallback

      if (typeof appointmenterror === 'string') {
        errorMessage = appointmenterror;
      } else if (appointmenterror?.message) {
        // Handle nested message object with en/fr properties
        if (typeof appointmenterror.message === 'object' && appointmenterror.message !== null) {
          errorMessage = appointmenterror.message[lang] || appointmenterror.message.en || appointmenterror.message.fr || errorMessage;
        } else if (typeof appointmenterror.message === 'string') {
          errorMessage = appointmenterror.message;
        }
      } else if (appointmenterror?.data?.message) {
        // Handle error.data.message structure
        if (typeof appointmenterror.data.message === 'object' && appointmenterror.data.message !== null) {
          errorMessage = appointmenterror.data.message[lang] || appointmenterror.data.message.en || appointmenterror.data.message.fr || errorMessage;
        } else if (typeof appointmenterror.data.message === 'string') {
          errorMessage = appointmenterror.data.message;
        }
      } else if (appointmenterror?.response?.data?.message) {
        // Handle old AxiosError format (shouldn't happen with extractErrorData, but just in case)
        if (typeof appointmenterror.response.data.message === 'object' && appointmenterror.response.data.message !== null) {
          errorMessage = appointmenterror.response.data.message[lang] || appointmenterror.response.data.message.en || appointmenterror.response.data.message.fr || errorMessage;
        } else if (typeof appointmenterror.response.data.message === 'string') {
          errorMessage = appointmenterror.response.data.message;
        }
      }

      toast.warn(errorMessage);
      dispatch(clearError());
      hasShownError.current = false;
    }
  }, [appointmenterror, dispatch, t, userRole, lang]);

  // Reset error flag when error is cleared
  useEffect(() => {
    if (!appointmenterror) {
      hasShownError.current = false;
    }
  }, [appointmenterror]);

  const loadingInitial = loadingDoctor || patientsLoading || (allAppointmentsLoading && isInitialAppointmentLoad.current);

  if (loadingInitial) {
    return (
      <div data-testid="appointment-loading-container" className="bg-white rounded-2xl p-4 shadow-md flex flex-col items-center justify-center" style={{ height: 'calc(100vh - 96px)' }}>
        <CommonLoader />
        <span data-testid="appointment-loading-text" className="text-xl text-[#4ABDBD]">{t('appointment.loading_message')}</span>
      </div>
    );
  }

  const specialitiesFulfilled = userRole === 'doctor'
    ? selectedDoctor?.appointmentSpeciality?.length > 0 && selectedDoctor.appointmentSpeciality.every(s => s.appointmentTypes?.length > 0)
    : true;

  const addressesFulfilled = userRole === 'doctor'
    ? selectedDoctor?.address?.length > 0 && selectedDoctor?.appointmentSpeciality?.length > 0 && selectedDoctor.appointmentSpeciality.every(s => s.appointmentTypes?.length > 0)
    : true;

  // Profile completion validation for doctors
  if (userRole === 'doctor') {
    // Get missing setup items using utility function
    const missingItemKeys = getDoctorMissingSetupItems(userRole, selectedDoctor);
    const missingItems = missingItemKeys.map(key => t(key));

    // If any required items are missing, show restriction page
    if (missingItems.length > 0) {
      return (
        <div data-testid="appointment-profile-validation-container" className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
          <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
            <img data-testid="appointment-profile-validation-image" src={errorimg} alt={t('profileValidation.altText')} className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain" />
            <h2 data-testid="appointment-profile-validation-title" className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">{t('profileValidation.title')}</h2>
            <p data-testid="appointment-profile-validation-message" className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
              {t('profileValidation.message')}
            </p>
            <div data-testid="appointment-profile-validation-missing-section" className="text-left bg-red-50 border border-red-200 rounded-lg p-4 mb-4 sm:mb-6 md:mb-8">
              <p data-testid="appointment-profile-validation-missing-label" className="text-xs sm:text-sm md:text-base text-gray-700 font-semibold mb-2">{t('profileValidation.missingItems')}:</p>
              <ul data-testid="appointment-profile-validation-missing-list" className="list-disc list-inside space-y-1 text-xs sm:text-sm md:text-base text-gray-600">
                {missingItems.map((item, index) => (
                  <li data-testid={`appointment-profile-validation-missing-item-${index}`} key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <button data-testid="appointment-profile-validation-goto-profile-button" onClick={() => navigate('/doctorProfile')} className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:from-[#3aa8a8] hover:to-[#2a7b7b] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 cursor-pointer">
              {t('profileValidation.goToProfile')}
            </button>
          </div>
        </div>
      );
    }
  }

  return (
    <div data-testid="appointment-page-container" className="bg-white rounded-2xl p-4 sm:p-6 shadow-md h-fit">
      {/* ==================== HEADER ==================== */}
      <div data-testid="appointment-header-section" className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
        <h1 data-testid="appointment-heading" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
          {t('appointment.heading')}
        </h1>

        {/* ---- ADMIN DOCTOR DROPDOWN (exact from AppointmentList) ---- */}
        {userRole === 'admin' && (
          <div data-testid="appointment-doctor-dropdown-container" ref={dropdownRef} className={`relative ${isSmallScreen ? 'w-full' : 'w-64'}`}>
            <button
              data-testid="appointment-doctor-dropdown-button"
              onClick={() => !loadingDoctor && setIsOpen(!isOpen)}
              disabled={loadingDoctor}
              className={`w-full px-4 py-2 border rounded-xl flex justify-between items-center font-medium transition
                ${loadingDoctor ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] cursor-pointer'}
                ${isSmallScreen ? 'text-sm' : ''}`}
            >
              <span data-testid="appointment-doctor-dropdown-selected-text" className="truncate w-[85%] text-left">
                {selectedDoctorState
                  ? selectedDoctorState.name
                  : t('appointment.select_doctor')}
              </span>
              <span className="ml-2 flex items-center">
                {loadingDoctor ? (
                  <span data-testid="appointment-doctor-dropdown-loading-spinner" className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                ) : isOpen ? (
                  <ChevronUp data-testid="appointment-doctor-dropdown-chevron-up-icon" />
                ) : (
                  <ChevronDown data-testid="appointment-doctor-dropdown-chevron-down-icon" />
                )}
              </span>
            </button>

            {isOpen && (
              <div data-testid="appointment-doctor-dropdown-panel" className={`absolute left-0 mt-2 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden ${isSmallScreen ? 'text-sm' : ''}`}>
                {/* Search */}
                <div data-testid="appointment-doctor-dropdown-search-container" className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <Search data-testid="appointment-doctor-dropdown-search-icon" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      data-testid="appointment-doctor-dropdown-search-input"
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      maxLength={50}
                      placeholder={t('appointment.search_doctor')}
                      className={`w-full pl-10 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${searchError
                        ? 'border-red-300 bg-red-50 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-[#4ABDBD]'
                        }`}
                      autoFocus
                    />
                  </div>
                  {searchError && (
                    <p data-testid="appointment-doctor-dropdown-search-error" className="text-xs text-red-600 mt-1 px-1">{searchError}</p>
                  )}
                </div>

                {/* Loading */}
                {loadingDoctor && (
                  <div data-testid="appointment-doctor-dropdown-list-loading" className="p-4 flex justify-center">
                    <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                  </div>
                )}

                {/* List */}
                {!loadingDoctor && (
                  <ul data-testid="appointment-doctor-dropdown-list" className="max-h-60 overflow-y-auto custom-scrollbar">
                    {/* <li onClick={handleReset} className="px-4 py-2.5 transition border-b border-gray-100 cursor-pointer hover:bg-[#4ABDBD] hover:text-white">
                      {t('appointment.select_doctor')}
                    </li> */}

                    {doctorNameList.length === 0 ? (
                      <li data-testid="appointment-doctor-dropdown-empty-text" className="px-4 py-3 text-gray-500 text-center">
                        {searchQuery ? t('appointment.no_doctors_found') : t('appointment.no_doctors_available')}
                      </li>
                    ) : (
                      doctorNameList.map((doc, idx) => (
                        <li
                          data-testid={`appointment-doctor-option-${doc.doctorId}`}
                          key={doc.doctorId}
                          onClick={() => doc.available && handleSelectDoctor(doc)}
                          className={`px-4 py-2.5 transition flex justify-between items-center
                            ${idx !== doctorNameList.length - 1 ? 'border-b border-gray-100' : ''}
                            ${doc.available ? 'cursor-pointer hover:bg-[#4ABDBD] hover:text-white' : 'cursor-not-allowed opacity-50 bg-gray-50'}`}
                        >
                          <span data-testid={`appointment-doctor-option-name-${doc.doctorId}`}>{doc.name}</span>
                          {!doc.available && <span data-testid={`appointment-doctor-option-unavailable-${doc.doctorId}`} className="text-xs text-red-500">({t('appointment.unavailable')})</span>}
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== TOGGLE SWITCH (Week View Only) ==================== */}
      {selectedDoctor && currentView === 'timeGridWeek' && (
        <div data-testid="appointment-week-toggle-section" className={`p-4 rounded-xl mb-4 flex justify-between items-center border ${weekToggleEnabled
          ? 'bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-[#4ABDBD]/30'
          : 'bg-gradient-to-r from-red-500/10 to-red-600/10 border-red-500/30'
          }`}>
          <div className="flex flex-col">
            <span data-testid="appointment-week-toggle-label" className="text-lg font-semibold text-gray-800">
              {t('appointment.week_calendar_toggle') || 'Enable Week Calendar'}
            </span>
            <span data-testid="appointment-week-toggle-status-text" className="text-sm text-gray-600">
              {weekToggleEnabled
                ? (t('appointment.week_enabled_message') || 'This week is enabled for online booking')
                : (t('appointment.week_disabled_message') || 'This week is disabled for online booking')}
            </span>
          </div>
          <label
            data-testid="appointment-week-toggle-switch-label"
            className="relative inline-flex items-center cursor-pointer group"
            onClick={(e) => {
              if (isPastWeek) {
                e.preventDefault();
                e.stopPropagation();
                toast.warn(t('appointment.past_week_error') || 'This is a past week, so it cannot be enabled or disabled');
              }
            }}
          >
            <input
              data-testid="appointment-week-toggle-input"
              type="checkbox"
              checked={weekToggleEnabled}
              onChange={(e) => {
                e.preventDefault();
                handleWeekToggleConfirm();
              }}
              disabled={rulesLoading || isPastWeek}
              className="sr-only peer"
            />
            <div className={`relative w-14 h-7 rounded-full transition-all duration-300 ease-in-out transform ${weekToggleEnabled
              ? 'bg-[#4ABDBD] shadow-md'
              : 'bg-gray-300 shadow-md'
              } ${rulesLoading || isPastWeek ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} peer-focus:outline-none peer-focus:ring-4 ${weekToggleEnabled
                ? 'peer-focus:ring-[#4ABDBD]/30'
                : 'peer-focus:ring-red-500/30'
              } group-hover:scale-105`}>
              <div className={`absolute top-[2px]  w-6 h-6 bg-white rounded-full shadow-lg transform transition-all duration-300 ease-in-out ${weekToggleEnabled
                ? 'translate-x-[30px] scale-100'
                : 'translate-x-0 scale-100 left-[2px]'
                } ${rulesLoading || isPastWeek ? '' : 'group-active:scale-90'}`}></div>
            </div>
          </label>
        </div>
      )}

      {/* ==================== NO DOCTOR SELECTED ==================== */}
      {userRole === 'admin' && !selectedDoctorState ? (
        <div data-testid="appointment-no-doctor-selected-container" className="flex items-center justify-center rounded-lg p-8" style={{ height: 'calc(100vh - 300px)' }}>
          <div className="text-center max-w-md">
            <div className="mb-4">
              <svg data-testid="appointment-no-doctor-selected-icon" className="mx-auto h-24 w-24 text-[#4ABDBD]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 data-testid="appointment-no-doctor-selected-title" className="text-xl font-semibold text-gray-700 mb-2">{t('appointment.no_doctor_selected_title')}</h3>
            <p data-testid="appointment-no-doctor-selected-text" className="text-gray-500">{t('appointment.select_doctor_to_view_calendar')}</p>
          </div>
        </div>
      ) : (
        /* ==================== CALENDAR ==================== */
        <div data-testid="appointment-calendar-container" className="relative">
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin, listPlugin]}
            initialView={'timeGridWeek'}
            events={allEvents}
            now={currentDate}
            initialDate={currentDate}
            firstDay={1}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            allDaySlot={false}
            height="calc(100vh - 110px)"
            slotDuration="00:15:00"
            slotLabelInterval="01:00"
            dayHeaderFormat={{ weekday: 'short', day: 'numeric', month: 'numeric' }}
            customButtons={{
              today: { text: t('appointment.today_button'), click: () => calendarRef.current.getApi().today() },
              dayGridMonth: { text: lang === 'fr' ? 'Mois' : 'Month', click: () => { const api = calendarRef.current.getApi(); api.changeView('dayGridMonth'); api.today(); } },
              timeGridWeek: { text: lang === 'fr' ? 'Semaine' : 'Week', click: () => { const api = calendarRef.current.getApi(); api.changeView('timeGridWeek'); api.today(); } },
              listWeek: { text: lang === 'fr' ? 'Liste' : 'List', click: () => { const api = calendarRef.current.getApi(); api.changeView('listWeek'); api.today(); } },
            }}
            headerToolbar={isSmallScreen ? {
              left: 'today',
              center: 'prev title next',
              right: userRole === 'doctor' ? 'dayGridMonth,timeGridWeek' : 'dayGridMonth,timeGridWeek',
            } : {
              left: 'today',
              center: 'prev title next',
              right: userRole === 'doctor' ? 'dayGridMonth,timeGridWeek' : 'dayGridMonth,timeGridWeek',
            }}
            views={{
              listWeek: { titleFormat: { year: 'numeric', month: 'short', day: 'numeric' }, listDayFormat: { weekday: 'short', day: 'numeric', month: 'short' }, listDaySideFormat: { hour: '2-digit', minute: '2-digit', hour12: false } },
              dayGridMonth: { eventDisplay: 'none' }
            }}
            dayCellClassNames={arg => {
              const classes = [];

              // Today's date highlight
              if (arg.date.toDateString() === currentDate.toDateString()) {
                classes.push('bg-yellow-200');
              }

              // Leave day — mark in month view
              if (arg.view.type === 'dayGridMonth' && selectedDoctor?.leaveDates?.length > 0) {
                const d = arg.date;
                const dayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                const dayLeaves = selectedDoctor.leaveDates.filter(leave => {
                  if (!leave.utcStart) return false;
                  const leaveDate = DateTime.fromISO(new Date(leave.utcStart).toISOString(), { zone: 'utc' }).setZone(systemTimeZone).toFormat('yyyy-MM-dd');
                  return leaveDate === dayStr;
                });
                if (dayLeaves.length > 0) {
                  const isFullDay = dayLeaves.some(l => l.startTime === '00:00' && l.endTime === '23:59');
                  const isForenoon = dayLeaves.some(l => l.startTime === '00:00' && (l.endTime === '11:59' || l.endTime === '12:00'));
                  const isAfternoon = dayLeaves.some(l => (l.startTime === '12:00') && l.endTime === '23:59');
                  if (isFullDay || (isForenoon && isAfternoon)) {
                    classes.push('fc-leave-day');
                  } else if (isForenoon) {
                    classes.push('fc-leave-forenoon');
                  } else if (isAfternoon) {
                    classes.push('fc-leave-afternoon');
                  }
                }
              }

              return classes.join(' ');
            }}
            editable={!!selectedDoctor && currentView === 'timeGridWeek'}
            selectable={!!selectedDoctor && currentView === 'timeGridWeek'}
            dateClick={(info) => {
              if (info.view.type === 'dayGridMonth') {
                const api = calendarRef.current.getApi();
                api.changeView('timeGridWeek', info.dateStr);
              }
            }}
            dayCellContent={(arg) => {
              if (arg.view.type === 'dayGridMonth' && appointments?.length > 0) {
                const d = arg.date;
                const dayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                const count = appointments.filter(appt => {
                  const apptTz = appt.timeZone || systemTimeZone;
                  const apptDate = DateTime.fromISO(appt.scheduledDate, { zone: 'utc' }).setZone(apptTz).toFormat('yyyy-MM-dd');
                  return apptDate === dayStr;
                }).length;
                const isToday = d.toDateString() === new Date().toDateString();
                return { html: `<div class="fc-month-cell-wrap"><div class="fc-daygrid-day-number ${isToday ? 'fc-month-today-badge' : ''}">${arg.dayNumberText}</div>${count > 0 ? `<div class="fc-month-appt-count">${count} ${count === 1 ? t('appointment.apt_count') : t('appointment.apts_count')}</div>` : ''}</div>` };
              }
            }}
            select={handleSelect}
            eventClick={handleEventClick}
            eventDrop={(info) => {
              // Remove tooltip when drop completes
              const existingTooltip = document.querySelector('.fc-event-tooltip');
              if (existingTooltip) existingTooltip.remove();
              // Reset dragging flag
              isDraggingRef.current = false;
              // Call the original handler
              handleEventDrop(info);
            }}
            eventDragStart={(info) => {
              // Set dragging flag
              isDraggingRef.current = true;
              // Remove tooltip when drag starts
              const existingTooltip = document.querySelector('.fc-event-tooltip');
              if (existingTooltip) existingTooltip.remove();
            }}
            eventDragStop={(info) => {
              // Reset dragging flag
              isDraggingRef.current = false;
              // Remove tooltip when drag stops (even if dropped or cancelled)
              const existingTooltip = document.querySelector('.fc-event-tooltip');
              if (existingTooltip) existingTooltip.remove();
            }}
            eventOverlap={(stillEvent) => {
              // Allow dragging an event onto a background event (break/leave/inactive)
              // when its corresponding restriction toggle is OFF — the grey shading stays
              // visible but the area becomes interactable. Regular events still cannot
              // overlap each other.
              if (stillEvent.display === 'background') {
                const restrictionType = stillEvent.extendedProps?.restrictionType;
                return !isBgEventRestricted({ restrictionType });
              }
              return false;
            }}
            eventConstraint={null}
            businessHours={getBusinessHours()}
            selectConstraint={selectedDoctor && selectedDoctor?.doctorSettings?.businessHour !== false ? 'businessHours' : null}
            datesSet={handleDatesSet}
            locale={lang === 'fr' ? frLocale : 'en'}
            eventAllow={(dropInfo, draggedEvent) => {
              if (!selectedDoctor) return false;
              const dropStart = DateTime.fromISO(dropInfo.startStr, { zone: systemTimeZone });
              const dropEnd = DateTime.fromISO(dropInfo.endStr, { zone: systemTimeZone });
              if (dropStart.toJSDate() < getDoctorPastBoundary(selectedDoctor?.doctorSettings, currentDate)) return false;

              // Check overlap with background events (breaks, leaves, unavailable)
              for (const bgEvent of backgroundEvents) {
                if (!isBgEventRestricted(bgEvent)) continue;
                const bgStart = new Date(bgEvent.start);
                const bgEnd = new Date(bgEvent.end);
                if (isOverlapping(dropStart.toJSDate(), dropEnd.toJSDate(), bgStart, bgEnd)) return false;
              }

              // Check overlap with other appointments (skip the dragged event itself)
              const draggedId = draggedEvent?.id || draggedEvent?._def?.publicId;
              for (const appt of appointments || []) {
                if (appt.appointmentId === draggedId) continue;
                const apptStart = DateTime.fromISO(appt.start, { zone: 'utc' }).setZone(systemTimeZone);
                const apptEnd = DateTime.fromISO(appt.end, { zone: 'utc' }).setZone(systemTimeZone);
                if (isOverlapping(dropStart.toJSDate(), dropEnd.toJSDate(), apptStart.toJSDate(), apptEnd.toJSDate())) return false;
              }

              return true;
            }}
            eventContent={arg => {
              // Hide events in month view — only show count badges
              if (arg.view.type === 'dayGridMonth') {
                return { html: '' };
              }
              if (arg.event.display === 'background') {
                // Show the public-holiday name (if any) on a line below the "Leave (…)"
                // label. Escape it — it's free text entered by the doctor.
                const holidayName = arg.event.extendedProps?.holidayName;
                const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
                const holidayHtml = holidayName
                  ? `<div class="disabled-slot-holiday" style="font-size: 0.85em; font-weight: 600; margin-top: 3px;">🏖️ ${escapeHtml(holidayName)}</div>`
                  : '';
                return { html: `<div class="disabled-slot-text" style="background-color: ${arg.event.backgroundColor};">${arg.event.title}${holidayHtml}</div>` };
              }
              const startTime = DateTime.fromISO(arg.event.startStr, { zone: systemTimeZone }).toFormat('HH:mm');
              const endTime = DateTime.fromISO(arg.event.endStr, { zone: systemTimeZone }).toFormat('HH:mm');
              const appointment = arg.event._def?.extendedProps;
              const isReschedulingStatus = appointment?.status === 'Rescheduling';
              const title = appointment?.appointTypeName || t('appointment.appointment_title');
              const displayTitle = isReschedulingStatus ? `${title} - ${t('appointment.rescheduling_suffix')}` : title;
              const eventColor = arg.event.backgroundColor;

              const isVisio = appointment?.appointmentMode === 'visio';
              const videoIcon = isVisio ? `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-left: 4px;"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>` : '';
              const isCurrentReschedulingEvent = isRescheduling && appointment?.appointmentId === selectedAppointment?.appointmentId;
              const opacityStyle = (isRescheduling && !isCurrentReschedulingEvent) ? 'opacity: 0.5; cursor: not-allowed;' : '';
              return { html: `<div class="event-content" style="background-color: ${eventColor}; border-color: ${eventColor}; border-radius: 4px;  display: inline-block; color: white; ${opacityStyle}">${startTime} - ${endTime}${videoIcon}</div>` };
            }}
            eventDidMount={info => {
              if (info.event.display === 'background') return;

              let tooltip = null;

              // Helper function to remove tooltip
              const removeTooltip = () => {
                if (tooltip && tooltip.parentNode) {
                  tooltip.remove();
                  tooltip = null;
                }
                // Also remove any orphaned tooltips
                const existingTooltip = document.querySelector('.fc-event-tooltip');
                if (existingTooltip) existingTooltip.remove();
              };

              // Show tooltip on mouse enter
              info.el.addEventListener('mouseenter', (e) => {
                // Don't show tooltip if dragging is in progress
                if (isDraggingRef.current) {
                  removeTooltip();
                  return;
                }
                // Remove any existing tooltip
                removeTooltip();

                // Get fresh appointment data from Redux store via ref
                const eventId = info.event._def.extendedProps?.appointmentId;
                const appointment = appointmentsRef.current?.find(appt => appt.appointmentId === eventId) || info.event._def.extendedProps;

                // Create tooltip
                tooltip = document.createElement('div');
                tooltip.className = 'fc-event-tooltip';

                // Get the event color for dynamic border and arrow
                const eventColor = info.event.backgroundColor || '#4ABDBD';
                tooltip.style.borderColor = eventColor;
                tooltip.style.setProperty('--tooltip-arrow-color', eventColor);

                const startTime = DateTime.fromISO(info.event.startStr, { zone: systemTimeZone }).toFormat('HH:mm');
                const endTime = DateTime.fromISO(info.event.endStr, { zone: systemTimeZone }).toFormat('HH:mm');
                const localDate = DateTime.fromISO(info.event.startStr, { zone: systemTimeZone }).toFormat('dd/MM/yyyy');

                const patientName = appointment.patientName && appointment.patientSurname
                  ? `${appointment.patientName} ${appointment.patientSurname}`
                  : appointment.patientName || '-';

                const statusClass = appointment.status ? appointment.status.toLowerCase().replace(/\s+/g, '') : 'booked';

                // For "Booked" status, use event color; for other statuses, use predefined colors
                const statusStyle = statusClass === 'booked' ? `style="background-color: ${eventColor};"` : '';

                tooltip.innerHTML = `
                  <div class="fc-event-tooltip-row">
                    <span class="fc-event-tooltip-label">${t('appointment.patient_label') || 'Patient'}:</span>
                    <span class="fc-event-tooltip-value">${patientName}</span>
                  </div>
                  <div class="fc-event-tooltip-row">
                    <span class="fc-event-tooltip-label">${t('appointment.appointment_type') || 'Type'}:</span>
                    <span class="fc-event-tooltip-value">${appointment.appointTypeName || '-'}</span>
                  </div>
                  <div class="fc-event-tooltip-row">
                    <span class="fc-event-tooltip-label">${t('appointment.date_label') || 'Date'}:</span>
                    <span class="fc-event-tooltip-value">${localDate}</span>
                  </div>
                  <div class="fc-event-tooltip-row">
                    <span class="fc-event-tooltip-label">${t('appointment.time_label') || 'Time'}:</span>
                    <span class="fc-event-tooltip-value">${startTime} - ${endTime}</span>
                  </div>
                  ${appointment.duration ? `
                  <div class="fc-event-tooltip-row">
                    <span class="fc-event-tooltip-label">${t('appointment.duration_label') || 'Duration'}:</span>
                    <span class="fc-event-tooltip-value">${appointment.duration} ${t('appointment.minutes') || 'min'}</span>
                  </div>` : ''}
                  ${appointment.appointmentMode ? `
                  <div class="fc-event-tooltip-row">
                    <span class="fc-event-tooltip-label">${t('appointment.mode_label') || 'Mode'}:</span>
                    <span class="fc-event-tooltip-value">${appointment.appointmentMode === 'onsite' ? t('appointment.onsite') || 'Onsite' : 'Visio'}</span>
                  </div>` : ''}
                  ${appointment.price ? `
                  <div class="fc-event-tooltip-row">
                    <span class="fc-event-tooltip-label">${t('appointment.price_label') || 'Price'}:</span>
                    <span class="fc-event-tooltip-value">
  ${appointment.currency === 'INR' ? '₹' : '€'} ${appointment.price}
</span>
                  </div>` : ''}
                  <div class="fc-event-tooltip-row">
                    <span class="fc-event-tooltip-label">${t('appointment.status_label') || 'Status'}:</span>
                    <span class="fc-event-tooltip-status ${statusClass}" ${statusStyle}>${appointment.status || 'Booked'}</span>
                  </div>
                `;

                document.body.appendChild(tooltip);

                // Position tooltip
                const rect = info.el.getBoundingClientRect();
                const tooltipRect = tooltip.getBoundingClientRect();

                let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
                let top = rect.top - tooltipRect.height - 12;
                let isBelow = false;

                // Adjust if tooltip goes off screen
                if (left < 10) left = 10;
                if (left + tooltipRect.width > window.innerWidth - 10) {
                  left = window.innerWidth - tooltipRect.width - 10;
                }
                if (top < 10) {
                  top = rect.bottom + 12;
                  isBelow = true;
                  tooltip.classList.add('tooltip-below');
                }

                tooltip.style.left = `${left}px`;
                tooltip.style.top = `${top}px`;
              });

              // Remove tooltip on mouse leave
              info.el.addEventListener('mouseleave', () => {
                removeTooltip();
              });

              // Remove tooltip when user starts dragging (mousedown)
              info.el.addEventListener('mousedown', () => {
                removeTooltip();
              });
            }}
          />

          <AppointmentModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            selectedSlot={selectedSlot}
            setSelectedSlot={setSelectedSlot}
            selectedAppointment={selectedAppointment}
            setSelectedAppointment={setSelectedAppointment}
            isRescheduling={isRescheduling}
            setIsRescheduling={setIsRescheduling}
            selectedDoctor={selectedDoctor}
            onReschedule={handleReschedulePayload}
            isModalOpenAppointment={isModalOpenAppointment}
            setisModalOpenAppointment={setisModalOpenAppointment}
            setAppoId={setAppoId}
            dateRangeStart={currentDateRange.current.start}
            dateRangeEnd={currentDateRange.current.end}
            onReschedulingSlotsRefetch={refetchReschedulingSlots}
          />

          <AppointmentDetailsModal
            isOpen={isDetailsModalOpen}
            onClose={() => { setIsDetailsModalOpen(false); setSelectedAppointment(null); }}
            appointment={selectedAppointment}
          />

          {isModalOpenAppointment && (
            <div data-testid="appointment-medical-history-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-8">
              <div data-testid="appointment-medical-history-modal-content" className="bg-white h-full rounded-lg p-6 w-full">
                <div className="flex justify-end items-center mb-4">
                  <button data-testid="appointment-medical-history-modal-close-button" onClick={handleCloseOpenAppointment} className="text-gray-500 hover:text-gray-700 cursor-pointer">
                    <X data-testid="appointment-medical-history-modal-close-icon" className="w-6 h-6" />
                  </button>
                </div>
                <MedicalHistory selected={appoId} isOpenAppointmentMode={true} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
